import React, {
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import BrowseGalleryOutlinedIcon from '@mui/icons-material/BrowseGalleryOutlined';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Popover from '@mui/material/Popover';
import IconButton from 'core/components/IconButton';
import OneTimeAnchoredHint from 'core/components/OneTimeAnchoredHint';
import Skeleton from 'core/components/Skeleton';
import ToolTip from 'core/components/Tooltip';
import {
  useNotify,
  useSidePanel,
} from 'core/hooks';
import ClockIcon from 'core/icons/clock';
import Button from 'pages/Dashboard/components/Button';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  OrgPCCActivationStatus,
} from 'dtos/orgPCCActivationStatus';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import {
  pccEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useEnrollPatientInPCC,
  useGetPCCActivationSettings,
  useGetPCCStatusByPatientId,
} from 'views/EMR/hooks/pcc';
import {
  PCC_MONTH_FORMAT,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/sidePanel/pccPanelUrl';
import PCCPopoverContent, {
  ActivatePCCTooltipContent,
  PCCActivationPendingContent,
  ProgramStatusIcons,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/PCCPopover';
import {
  getEligiblePCCPrograms,
  getPCCPrograms,
  hasActionablePCCPrograms,
  PCCProgramStatus,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/utils';
import useSignalREffect from 'views/EMR/hooks/signalR';
import PCCIcon from 'core/icons/pcc';
import dayjs from 'utils/dayjs';

type Props = {
  patient: Patient;
  patientId: number;
  encounterId: number;
  visitNoteId: number;
  onBeforeEnroll: () => Promise<boolean>;
};

type ActivatePCCButtonProps = {
  isCcmApplicable: boolean;
  onActivate: () => void;
};

function ActivatePCCButton({
  isCcmApplicable,
  onActivate,
}: ActivatePCCButtonProps) {
  return (
    <ToolTip
      isInteractive
      title={<ActivatePCCTooltipContent isCcmApplicable={isCcmApplicable} />}
    >
      <span>
        <Button
          className="flex h-6 items-center gap-1.5 !border-blue-500 !px-2.5 !py-0.5"
          variant="outlined"
          color="primary"
          type="button"
          onClick={onActivate}
        >
          <PCCIcon className="!h-5 !w-5 !text-blue-500" />
          <span className="text-xs font-semibold">Activate PCC</span>
        </Button>
      </span>
    </ToolTip>
  );
}

type ActivationRequestedButtonProps = {
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
};

function ActivationRequestedButton({
  onOpen,
}: ActivationRequestedButtonProps) {
  return (
    <ToolTip title="View PCC activation status">
      <span>
        <Button
          className="flex h-6 items-center gap-1.5 !border-gray-300 !px-2.5 !py-0.5 !text-gray-700"
          variant="outlined"
          color="inherit"
          type="button"
          onClick={onOpen}
        >
          <ClockIcon className="!h-3.5 !w-3.5 !text-amber-600" />
          <span className="text-xs font-semibold">Activation requested</span>
        </Button>
      </span>
    </ToolTip>
  );
}

type PCCStatusButtonProps = {
  hasAction: boolean;
  programs: PCCProgramStatus[];
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
};

function PCCStatusButton({
  hasAction,
  programs,
  onOpen,
}: PCCStatusButtonProps) {
  return (
    <ToolTip title="View PCC program status">
      <span>
        <Button
          className={clsx(
            'flex h-6 items-center gap-1.5 !px-2.5 !py-0.5',
            {
              '!border-blue-500': hasAction,
            },
          )}
          variant="outlined"
          color="primary"
          type="button"
          onClick={onOpen}
        >
          <span className="flex items-center gap-2">
            <span className="text-xs font-semibold">PCC</span>
            <ProgramStatusIcons programs={programs} />
          </span>
        </Button>
      </span>
    </ToolTip>
  );
}

type PCCDetailsButtonProps = {
  onOpen: () => void;
};

function PCCDetailsButton({ onOpen }: PCCDetailsButtonProps) {
  return (
    <IconButton
      aria-label="Open PCC patient details"
      tooltip="Open PCC patient details"
      size="small"
      onClick={onOpen}
      color='info'
    >
      <BrowseGalleryOutlinedIcon className="!h-5 !w-5 !text-blue-500" />
    </IconButton>
  );
}

type PCCControlProps = ActivatePCCButtonProps & PCCStatusButtonProps & {
  isActivated: boolean;
  isPending: boolean;
  onOpenDetails: () => void;
};

function PCCControl({
  isActivated,
  isPending,
  isCcmApplicable,
  hasAction,
  programs,
  onActivate,
  onOpen,
  onOpenDetails,
}: PCCControlProps) {
  const [activateHintAnchorEl, setActivateHintAnchorEl] = useState<HTMLElement | null>(null);
  const [statusHintAnchorEl, setStatusHintAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      {isActivated ? (
        <div className="flex items-center gap-1" aria-label="PCC control">
          <span ref={setStatusHintAnchorEl}>
            <PCCStatusButton
              hasAction={hasAction}
              programs={programs}
              onOpen={onOpen}
            />
          </span>
          <PCCDetailsButton onOpen={onOpenDetails} />
          <OneTimeAnchoredHint
            storageKey="browser:visit-note-pcc-status-hint"
            anchorEl={statusHintAnchorEl}
            show={isActivated}
            placement="bottom-end"
          >
            Track every program and enroll eligible patients from here.
          </OneTimeAnchoredHint>
        </div>
      ) : null}
      {isPending ? (
        <ActivationRequestedButton onOpen={onOpen} />
      ) : null}
      {!isActivated && !isPending ? (
        <>
          <span ref={setActivateHintAnchorEl}>
            <ActivatePCCButton
              isCcmApplicable={isCcmApplicable}
              onActivate={onActivate}
            />
          </span>
          <OneTimeAnchoredHint
            storageKey="browser:visit-note-pcc-activate-hint"
            anchorEl={activateHintAnchorEl}
            show={!isActivated && !isPending}
            placement="bottom-end"
          >
            Extend care beyond the visit. Activate PCC to start enrolling patients.
          </OneTimeAnchoredHint>
        </>
      ) : null}
    </>
  );
}

export default function PCC({
  patient,
  patientId,
  encounterId,
  visitNoteId,
  onBeforeEnroll,
}: Props) {
  const notify = useNotify();
  const { openPanel } = useSidePanel();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPreparingEnrollment, setIsPreparingEnrollment] = useState(false);
  const {
    data: activationSettings,
    isLoading: isLoadingActivationSettings,
  } = useGetPCCActivationSettings();

  const {
    data: PCCStatus,
    isFetching: isFetchingStatus,
    refetch: refetchPatientStatus,
  } = useGetPCCStatusByPatientId(patientId);

  useSignalREffect(
    'OnPCCEligibilityUpdated',
    ({ patientId: signaledPatientId }) => {
      if (signaledPatientId === patientId) {
        refetchPatientStatus();
      }
    },
    [patientId, refetchPatientStatus],
  );

  const {
    mutateAsync: enrollPatient,
    isPending: isEnrolling,
  } = useEnrollPatientInPCC();

  const activationStatus = activationSettings?.activationStatus;
  const isActivated = activationStatus === OrgPCCActivationStatus.Active;
  const isPending = activationStatus === OrgPCCActivationStatus.Pending;
  const isCcmApplicable = activationSettings?.isCcmApplicable !== false;
  const programs = useMemo(
    () => getPCCPrograms(PCCStatus, isCcmApplicable),
    [PCCStatus, isCcmApplicable],
  );
  const isConsented = Boolean(PCCStatus?.ccm?.isConsented);
  const needsConsent = isCcmApplicable && !isConsented;
  const hasAction = hasActionablePCCPrograms(programs) || needsConsent;
  const showStatusSkeleton = isLoadingActivationSettings
    || (isActivated && isNil(PCCStatus) && isFetchingStatus);
  const isPopoverOpen = !isNil(anchorEl) && (isActivated || isPending);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleActivatePCC = useCallback(() => {
    pccEventChannel.emit('onShowPCCWalkthrough', {
      showReturnToVisitNote: true,
    });
  }, []);

  const handleOpenDetails = useCallback(() => {
    openPanel('pcc', {
      patientId,
      month: dayjs().format(PCC_MONTH_FORMAT),
    });
  }, [openPanel, patientId]);

  const handleEnrollSelected = useCallback(async (programsToEnroll: PCCProgram[]) => {
    setIsPreparingEnrollment(true);
    try {
      const canContinue = await onBeforeEnroll();
      if (!canContinue) {
        return;
      }

      if (programsToEnroll.includes(PCCProgram.RTM)) {
        handleClose();
        const eligible = getEligiblePCCPrograms(programs).map((program) => program.type);
        pccEventChannel.emit('onShowPCCEnroll', {
          patientId,
          encounterId,
          visitNoteId,
          programs: eligible,
          selectedPrograms: programsToEnroll,
          variant: 'recommendation',
        });
        return;
      }

      try {
        await enrollPatient({
          patientId,
          encounterId,
          programs: programsToEnroll,
        });
        await refetchPatientStatus();
        notify({
          variant: 'success',
          message: programsToEnroll.length > 1
            ? 'Patient enrolled in selected PCC programs'
            : `Patient enrolled in ${programsToEnroll[0]}`,
        });
      } catch (error) {
        console.error(error);
      }
    } finally {
      setIsPreparingEnrollment(false);
    }
  }, [
    encounterId,
    enrollPatient,
    handleClose,
    notify,
    onBeforeEnroll,
    patientId,
    programs,
    refetchPatientStatus,
    visitNoteId,
  ]);

  return (
    <>
      {showStatusSkeleton ? (
        <Skeleton className="!h-6 !w-24" />
      ) : (
        <PCCControl
          isActivated={isActivated}
          isPending={isPending}
          isCcmApplicable={isCcmApplicable}
          hasAction={hasAction}
          programs={programs}
          onActivate={handleActivatePCC}
          onOpen={handleOpen}
          onOpenDetails={handleOpenDetails}
        />
      )}
      {isActivated || isPending ? (
        <Popover
          id="visit-note-pcc-popover"
          anchorEl={anchorEl}
          open={isPopoverOpen}
          onClose={handleClose}
          slotProps={{
            paper: {
              className: '!overflow-visible !shadow-lg',
            },
          }}
          anchorOrigin={{
            vertical: (anchorEl?.offsetHeight ?? 0) + 4,
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {isPending ? (
            <PCCActivationPendingContent />
          ) : null}
          {isActivated ? (
            <PCCPopoverContent
              isEnrolling={isEnrolling || isPreparingEnrollment}
              programs={programs}
              isConsented={isConsented}
              patientId={patientId}
              patientEmail={patient.email}
              patientPhone={patient.phoneNumber?.phoneNumberWithAreaCode}
              onEnrollSelected={handleEnrollSelected}
            />
          ) : null}
        </Popover>
      ) : null}
    </>
  );
}
